import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Pressable, View } from 'react-native'
import { connect } from 'react-redux'

import { format, parseISO } from 'date-fns'
import { includes, isEmpty, isEqual, isInteger, isNumber } from 'lodash-es'
import { AnyAction, bindActionCreators, Dispatch } from 'redux'
import styled, { useTheme } from 'styled-components/native'

import { infoButtonLabel } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import { getCHRTHighRiskDetails } from '@lyrahealth-inc/shared-app-logic/src/features/highAlert/utils'
import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { Assignment } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import {
  InfoPopoverPlacement,
  InfoPopoverTriggerAction,
} from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import {
  Outcome,
  QuestionResponse,
  ResendOutcomeResponse,
  Scores,
} from '@lyrahealth-inc/shared-app-logic/src/models/outcome/Outcome'
import { ProviderAdminProviderInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import SecondaryButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/secondaryButton/SecondaryButton'
import LoadingIndicator from '@lyrahealth-inc/ui-core/lib/atoms/loadingIndicator/LoadingIndicator'
import { outcomeChartConstants } from '@lyrahealth-inc/ui-core/lib/constants/outcomeConstants'
import OutcomeResponseDetails from '@lyrahealth-inc/ui-core/lib/molecules/outcomeResponseDetails/OutcomeResponseDetails'
import outcomeUtils from '@lyrahealth-inc/ui-core/lib/utils/outcomeUtils'
import { ButtonType } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/baseButton/BaseButton'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { DropdownButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/dropdownButton/DropdownButton'
import {
  TypeAheadCustomOptionsConfig,
  ValueFetchMode,
} from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/typeAhead/TypeAheadCustomOptionsConfig'
import { HeaderText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/headerText/HeaderText'
import { ChartIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChartIcon'
import { InfoIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InfoIcon'
import { InfoPopover } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/infoPopover/InfoPopover'
import { TextButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/textButton/TextButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BaseModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/baseModal/BaseModal'
import { colors } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/colors'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { AlliancePopoverContent, PHQPopoverContent, SelfHarmPopoverContent } from './AssessmentPopovers'
import styles from './clientDetails.module.scss'
import { LeapsResultsEntry } from './LeapsResultsEntry'
import { OutcomeHistoryCharts } from './OutcomeHistoryCharts'
import { RoiResultsEntry } from './RoiResultsEntry'
import { SUBSCALE_RESULTS_CONFIGS, SubscaleResultsEntry } from './SubscaleResultsEntry'
import { trackClicks } from '../../../../mixpanel/mixpanelTracking'
import { SUPERVISOR_ROLES } from '../../common/constants/appConstants'
import { hasRole } from '../../common/utils/utils'
import { addAlert } from '../../data/alertActions'
import { RootState } from '../../data/store'
import { getProviderDetailsData } from '../../providers/data/providerSelectors'
import { resendOutcomeEmail } from '../data/clientsDataActions'

const NoDataContainer = styled.View({
  background: colors.white,
  border: `1px solid ${colors.charcoal2}`,
  borderRadius: '12px',
  padding: '15px',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '50px',
  clear: 'both',
})

const TextContainer = styled.View({
  textAlign: 'center',
  maxWidth: '300px',
})

const HeaderTextContainer = styled.View({
  margin: '20px 0 10px',
})

const OutcomeControlsContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginBottom: spacing['40px'],
  zIndex: 2,
}))

const AssessmentTrackSettingsContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  alignItems: 'flex-end',
  marginBottom: spacing['16px'],
}))

const SelectOutcomeContainer = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: colors.backgroundElement,
  borderRadius: '16px',
  padding: spacing['24px'],
  gap: spacing['32px'],
  width: '100%',
}))

const DropdownsGroup = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['16px'],
  flexShrink: 0,
}))

const OatmealDropdownButton = styled(DropdownButton)<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  borderRadius: 8,
  borderColor: colors.secondaryButtonOatmealOutlineDefault,
  backgroundColor: colors.secondaryButtonOatmealBackgroundDefault,
}))

const DateContainer = styled.View<{ theme: ThemeType; withLeftBorder?: boolean }>(
  ({ theme: { colors, spacing }, withLeftBorder }) => ({
    paddingLeft: spacing['32px'],
    borderLeftWidth: withLeftBorder ? `1px` : undefined,
    borderLeftColor: withLeftBorder ? colors.borderDefault : undefined,
    marginLeft: withLeftBorder ? spacing['32px'] : undefined,
  }),
)

const OutcomesResultsContainer = styled.View<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  borderRadius: '12px',
  borderColor: colors.borderDefault,
  borderWidth: '1px',
  overflow: 'hidden',
}))

const HeaderContainer = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  borderBottomWidth: '1px',
  borderBottomColor: colors.borderDefault,
  backgroundColor: colors.backgroundElement,
  padding: `${spacing['12px']} ${spacing['24px']}`,
}))

const AssessmentContentContainer = styled.View({
  flexDirection: 'row',
  flexBasis: '320px',
  alignItems: 'center',
})

const ScoreContentContainer = styled.View({
  flexDirection: 'row',
  flexBasis: '80px',
})

const TableRow = styled.View<{ theme: ThemeType; alignTop?: boolean; isLastItem?: boolean }>(
  ({ theme: { colors, spacing }, alignTop, isLastItem }) => ({
    flexDirection: 'row',
    alignItems: alignTop ? 'flex-start' : 'center',
    padding: spacing['24px'],
    borderBottomWidth: isLastItem ? '0' : '1px',
    borderBottomColor: isLastItem ? undefined : colors.borderDefault,
  }),
)

const QuestionContainer = styled.View<{ theme: ThemeType; columnLayout?: boolean }>(
  ({ theme: { spacing }, columnLayout }) => ({
    flexDirection: columnLayout ? 'column' : 'row',
    alignItems: columnLayout ? 'flex-start' : 'center',
    marginTop: spacing['8px'],
  }),
)

const SelfHarmColumn = styled.View({
  flex: 1,
})

const SelfHarmContentContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['16px'],
}))

const ViewAnswersButton = styled(TextButton)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginLeft: spacing['8px'],
  marginTop: spacing['6px'],
}))

const InfoIconContainer = styled(Pressable)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginLeft: spacing['4px'],
  marginTop: spacing['4px'],
  zIndex: 1,
}))

const SelfReflectionContentContainer = styled.View({
  flex: 1,
})

const ResultsHeadingContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginBottom: spacing['16px'],
}))

const ClientOutcomes: React.FC<ClientOutcomesProps> = ({
  clientFirstName,
  clientFullName,
  consentStatus,
  consentSubmitted,
  isRegistered,
  learnMore,
  outcomes,
  patient_lyra_id,
  providerDetailsData,
  reminder,
  title,
  assignmentsWithSelfReflection,
  selectedProgressEpisode,
  assessmentTrackSettings,
  recipientOptions,
  selectedRecipientLyraId,
  onRecipientChange,
  // @ts-expect-error TS(2339): Property 'addAlert' does not exist on type '{ rese... Remove this comment to see the full error message
  actions: { addAlert, resendOutcomeEmail },
}) => {
  const intl = useIntl()
  const { formatMessage } = intl
  const theme = useTheme()
  const [showModal, setShowModal] = useState(false)
  const [modalData, setModalData] = useState<Response | null>(null)
  const [selectedOutcome, setSelectedOutcome] = useState(outcomes ? outcomes[0] : [])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    trackClicks('ClientOutcomes')
  }, [])

  useEffect(() => {
    if (recipientOptions) {
      const submittedOutcomes = outcomes?.filter((outcome: Outcome) => outcome.response_date).reverse()
      setSelectedOutcome(submittedOutcomes ? submittedOutcomes[0] : [])
    } else {
      setSelectedOutcome(outcomes ? outcomes[0] : [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgressEpisode, outcomes])

  const selectedRecipientName =
    recipientOptions && selectedRecipientLyraId
      ? [recipientOptions.teen, recipientOptions.caregiver]
          .filter((r): r is RecipientOption => Boolean(r))
          .find((r) => r.lyraId === selectedRecipientLyraId)?.name
      : undefined
  const isCaregiverSelected = recipientOptions?.caregiver?.lyraId === selectedRecipientLyraId

  // Handle empty outcomes case
  const hasOutcomes = outcomes && !isEmpty(outcomes)

  const renderNoDataContent = () => (
    <NoDataContainer testID={tID('ClientProgress-noData')}>
      <TextContainer>
        <HeaderTextContainer>
          <HeaderText
            text={formatMessage({ defaultMessage: 'No data yet', description: 'no data title' })}
            size={TitleSize.MEDIUM}
            textAlign='center'
          />
        </HeaderTextContainer>
        <BodyText
          text={formatMessage({
            defaultMessage:
              "Your client hasn't submitted assessments yet. Encourage your client to complete them and check back to track their progress.",
            description: 'label indicating that there is no data available from the client',
          })}
          textAlign='center'
        />
      </TextContainer>
      <ChartIcon />
    </NoDataContainer>
  )

  const showAnswersModal = (modalData: Response) => {
    setShowModal(true)
    setModalData(modalData)
  }

  const onViewButtonClick = (assessmentType: string) => {
    const outcome = selectedOutcome
    const answers = (outcome as any).response_details
    const modalData = {
      clientName: clientFullName ?? '',
      title: outcomeChartConstants.find((obj) => obj.name === assessmentType)?.fullName || '',
      dateStarted: (outcome as any).date_created ? format(parseISO((outcome as any).date_created), 'MM/dd/yyyy') : '',
      dateSubmitted: (outcome as any).response_date
        ? format(parseISO((outcome as any).response_date), 'MM/dd/yyyy')
        : '',
      assessmentType: assessmentType,
      score: (outcome as any).scores[assessmentType],
      answers: answers.filter((a: QuestionResponse) => includes(a.groups, assessmentType)),
    }
    showAnswersModal(modalData)
  }

  const resendOutcomeClickHandler = () => {
    setLoading(true)
    resendOutcomeEmail(patient_lyra_id, providerDetailsData?.lyra_id)
      .then(
        (resendOutcomeResponse: ResendOutcomeResponse) => {
          return addAlert({
            show: true,
            contents: resendOutcomeResponse.message,
            style: resendOutcomeResponse.status === 'error' ? 'danger' : 'success',
            expires: true,
          })
        },
        () => {},
      )
      .finally(() => {
        setLoading(false)
      })
  }

  const renderOutcomeResultsEntry = (
    assessmentName: string,
    assessmentTitle: string = assessmentName,
    isLastItem = false,
  ) => {
    const describeScore = outcomeUtils.convertOutcomeScoreToDescription
    const scores = (selectedOutcome as any)?.scores
    return (
      <TableRow testID={tID(`ClientOutcomes-results-${assessmentName}`)} isLastItem={isLastItem}>
        <AssessmentContentContainer>
          <Title text={assessmentTitle} size={TitleSize.MEDIUM} />
          <ViewAnswersButton
            testID={tID(`ClientOutcomes-viewAnswers-${assessmentName.toLowerCase()}`)}
            onPress={() => {
              onViewButtonClick(assessmentName)
            }}
            aria-label={`View answers for ${assessmentTitle || assessmentName} on ${format(
              parseISO((selectedOutcome as any).response_date),
              'MM/dd/yyyy',
            )}`}
            text='view answers'
          />
        </AssessmentContentContainer>
        <ScoreContentContainer>
          <Title testID={tID(`client-${assessmentName}-score`)} text={scores[assessmentName]} size={TitleSize.MEDIUM} />
        </ScoreContentContainer>
        <View>
          <Title
            testID={tID(`client-${assessmentName}-score-description`)}
            text={describeScore(assessmentName, scores[assessmentName])}
            size={TitleSize.MEDIUM}
          />
        </View>
      </TableRow>
    )
  }

  const renderChrtEntry = (scoreKey: 'CHRT' | 'CHRT-SR-9-TOTAL', title: string, testIdSlug: string) => {
    const describeScore = outcomeUtils.convertOutcomeScoreToDescription
    const scores = (selectedOutcome as Outcome)?.scores
    const chrtHighRiskDetails = getCHRTHighRiskDetails((selectedOutcome as Outcome)?.response_details)
    return (
      <TableRow testID={tID(`ClientOutcomes-results-${title}`)} alignTop={chrtHighRiskDetails.length > 0}>
        <AssessmentContentContainer>
          <Title text={title} size={TitleSize.MEDIUM} />
          <ViewAnswersButton
            testID={tID(`ClientOutcomes-viewAnswers-${testIdSlug}`)}
            onPress={() => {
              onViewButtonClick(scoreKey)
            }}
            aria-label={`View answers for ${title} on ${format(
              parseISO((selectedOutcome as Outcome).response_date),
              'MM/dd/yyyy',
            )}`}
            text='view answers'
          />
        </AssessmentContentContainer>
        <ScoreContentContainer>
          <Title testID={tID(`client-${testIdSlug}-score`)} text={scores[scoreKey]} size={TitleSize.MEDIUM} />
        </ScoreContentContainer>
        <SelfHarmColumn>
          <Title
            testID={tID(`client-${testIdSlug}-score-description`)}
            text={describeScore(scoreKey, scores[scoreKey])}
            size={TitleSize.MEDIUM}
          />
          {chrtHighRiskDetails.map((detail) => (
            <SelfHarmContentContainer key={detail['question-number']}>
              <QuestionContainer columnLayout>
                <Title text={detail.question} size={TitleSize.XSMALL} />
                <BodyText text={detail['answer-text']} size={BodyTextSize.DEFAULT} color={theme.colors.textError} />
              </QuestionContainer>
            </SelfHarmContentContainer>
          ))}
        </SelfHarmColumn>
      </TableRow>
    )
  }

  const renderOutcomeResults = () => {
    const describeScore = outcomeUtils.convertOutcomeScoreToDescription
    const scores = (selectedOutcome as Outcome)?.scores
    let assignmentForSelectedOutcome

    const programName = (selectedOutcome as Outcome)?.program_name?.toLowerCase().includes('coaching')
      ? 'Coaching'
      : 'Therapy'
    const providerType = programName === 'Therapy' ? 'therapist' : 'coach'
    if (selectedOutcome && 'assignment_id' in selectedOutcome) {
      assignmentForSelectedOutcome = assignmentsWithSelfReflection?.find(
        (assignment) => assignment.id === selectedOutcome?.assignment_id,
      )
    }
    const selfReflectionText = assignmentForSelectedOutcome?.assignment_responses?.[0]?.response?.self_reflection
    const shouldProviderViewAllianceScores =
      (selectedOutcome as Outcome)?.provider_lyra_id === providerDetailsData?.id ||
      hasRole(providerDetailsData?.roles, SUPERVISOR_ROLES)

    if (scores) {
      const renderedRows: React.ReactNode[] = []

      // Define the order and rendering logic for each score type
      const scoreConfig: Record<string, { title?: string; render: () => React.ReactNode }> = {
        CGSQ: { render: () => renderOutcomeResultsEntry('CGSQ', 'CGSQ') },
        'PARENTAL-STRESS': { render: () => renderOutcomeResultsEntry('PARENTAL-STRESS', 'PARENTAL STRESS') },
        'PARENTING-TASK': { render: () => renderOutcomeResultsEntry('PARENTING-TASK', 'PARENTING TASK') },
        'RCADS-ANXIETY': { render: () => renderOutcomeResultsEntry('RCADS-ANXIETY', 'RCADS: Anxiety') },
        'RCADS-DEPRESSION': { render: () => renderOutcomeResultsEntry('RCADS-DEPRESSION', 'RCADS: Depression') },
        'RCADS-TOTAL': { render: () => renderOutcomeResultsEntry('RCADS-TOTAL', 'RCADS: Total') },
        'RCADS-P-ANXIETY': { render: () => renderOutcomeResultsEntry('RCADS-P-ANXIETY', 'RCADS-P: Anxiety') },
        'RCADS-P-DEPRESSION': { render: () => renderOutcomeResultsEntry('RCADS-P-DEPRESSION', 'RCADS-P: Depression') },
        'RCADS-P-TOTAL': { render: () => renderOutcomeResultsEntry('RCADS-P-TOTAL', 'RCADS-P: Total') },
        TFM: { render: () => renderOutcomeResultsEntry('TFM') },
        'TLFB-ALCOHOL': { render: () => renderOutcomeResultsEntry('TLFB-ALCOHOL', 'TLFB Alcohol') },
        'TLFB-OTHER-SUBSTANCES': {
          render: () => renderOutcomeResultsEntry('TLFB-OTHER-SUBSTANCES', 'TLFB Substances'),
        },
        BDD: { render: () => renderOutcomeResultsEntry('BDD') },
        PACS: { render: () => renderOutcomeResultsEntry('PACS') },
        ADCS: { render: () => renderOutcomeResultsEntry('ADCS') },
        WSAS: { render: () => renderOutcomeResultsEntry('WSAS') },
        PCL5: { render: () => renderOutcomeResultsEntry('PCL5', 'PCL5') },
        CHRT: { render: () => renderChrtEntry('CHRT', 'CHRT', 'chrt') },
        'CHRT-SR-9-TOTAL': { render: () => renderChrtEntry('CHRT-SR-9-TOTAL', 'CHRT-SR-9', 'chrt-sr-9') },
        GAD: { render: () => renderOutcomeResultsEntry('GAD', 'GAD7') },
        PHQ: {
          render: () => (
            <TableRow testID={tID('ClientOutcomes-results-PHQ')} alignTop>
              <AssessmentContentContainer>
                <Title text='PHQ9' size={TitleSize.MEDIUM} />
                <ViewAnswersButton
                  testID={tID('ClientOutcomes-viewAnswers-phq9')}
                  onPress={() => {
                    onViewButtonClick('PHQ')
                  }}
                  aria-label={`View answers for PHQ9 on ${format(
                    parseISO((selectedOutcome as any).response_date),
                    'MM/dd/yyyy',
                  )}`}
                  text='view answers'
                />
              </AssessmentContentContainer>
              <ScoreContentContainer>
                <Title testID={tID('client-phq-score')} text={scores.PHQ} size={TitleSize.MEDIUM} />
              </ScoreContentContainer>
              <SelfHarmColumn>
                <Title
                  testID={tID('client-phq-score-description')}
                  text={describeScore('PHQ', scores.PHQ)}
                  size={TitleSize.MEDIUM}
                />
                <QuestionContainer>
                  <Title text='Life Impairment Question' size={TitleSize.XSMALL} />
                  <InfoPopover
                    content={<PHQPopoverContent />}
                    trigger={
                      <InfoIconContainer accessibilityRole='button' accessibilityLabel={formatMessage(infoButtonLabel)}>
                        <InfoIcon size={16} fillColor={theme.colors.iconActive} />
                      </InfoIconContainer>
                    }
                    placement={InfoPopoverPlacement.TOP}
                    popoverTriggerAction={InfoPopoverTriggerAction.PRESS}
                    shouldDisplayCloseButton={false}
                    popoverStyle={{ container: { width: '545px' } }}
                  />
                </QuestionContainer>
                <BodyText
                  text={describeScore('PHQ-LIFE', scores['PHQ-LIFE'])}
                  size={BodyTextSize.DEFAULT}
                  color={theme.colors.textSecondary}
                />
                {scores['PHQ-SELF-HARM'] ? (
                  <SelfHarmContentContainer>
                    <QuestionContainer>
                      <Title text='Thoughts of self harm and/or suicide' size={TitleSize.XSMALL} />
                      <InfoPopover
                        content={<SelfHarmPopoverContent />}
                        trigger={
                          <InfoIconContainer
                            accessibilityRole='button'
                            accessibilityLabel={formatMessage(infoButtonLabel)}
                          >
                            <InfoIcon size={16} fillColor={theme.colors.iconActive} />
                          </InfoIconContainer>
                        }
                        placement={InfoPopoverPlacement.TOP}
                        popoverTriggerAction={InfoPopoverTriggerAction.PRESS}
                        shouldDisplayCloseButton={false}
                        popoverStyle={{ container: { width: '545px' } }}
                      />
                    </QuestionContainer>
                    <BodyText
                      text={describeScore('PHQ-SELF-HARM', scores['PHQ-SELF-HARM'])}
                      size={BodyTextSize.DEFAULT}
                      color={theme.colors.textError}
                    />
                  </SelfHarmContentContainer>
                ) : null}
              </SelfHarmColumn>
            </TableRow>
          ),
        },
        PSS: { render: () => renderOutcomeResultsEntry('PSS') },
        'WELL-BEING': { render: () => renderOutcomeResultsEntry('WELL-BEING', 'Well Being') },
        FTND: { render: () => renderOutcomeResultsEntry('FTND', 'FTND') },
        'E-FTND': { render: () => renderOutcomeResultsEntry('E-FTND', 'E-FTND') },
      }

      Object.keys(scoreConfig).forEach((scoreKey) => {
        const scoreValue = scores[scoreKey as keyof Scores]
        const config = scoreConfig[scoreKey]

        if (config && (isNumber(scoreValue) || isInteger(scoreValue))) {
          renderedRows.push(config.render())
        }
      })

      // Handle alliance scores
      if (
        (isInteger(scores['SATISFACTION-GOALS']) ||
          isInteger(scores['SATISFACTION-RELATIONSHIP']) ||
          isInteger(scores['SATISFACTION-THERAPY'])) &&
        shouldProviderViewAllianceScores
      ) {
        renderedRows.push(
          <TableRow testID={tID('ClientOutcomes-results-alliance')} alignTop>
            <AssessmentContentContainer>
              <Title text='Alliance Questions' size={TitleSize.MEDIUM} />
              <InfoPopover
                content={<AlliancePopoverContent programName={programName} providerType={providerType} />}
                trigger={
                  <InfoIconContainer accessibilityRole='button' accessibilityLabel={formatMessage(infoButtonLabel)}>
                    <InfoIcon size={16} fillColor={theme.colors.iconActive} />
                  </InfoIconContainer>
                }
                placement={InfoPopoverPlacement.TOP}
                popoverTriggerAction={InfoPopoverTriggerAction.PRESS}
                shouldDisplayCloseButton={false}
                popoverStyle={{ container: { width: '545px' } }}
              />
            </AssessmentContentContainer>
            <ScoreContentContainer />
            <View>
              <Title text={`${programName} Approach Satisfaction`} size={TitleSize.XSMALL} />
              <BodyText
                text={describeScore('SATISFACTION', scores['SATISFACTION-GOALS'])}
                size={BodyTextSize.DEFAULT}
                color={theme.colors.textSecondary}
              />
              <QuestionContainer columnLayout>
                <Title text='Relationship Satisfaction' size={TitleSize.XSMALL} />
                <BodyText
                  text={describeScore('SATISFACTION', scores['SATISFACTION-RELATIONSHIP'])}
                  size={BodyTextSize.DEFAULT}
                  color={theme.colors.textSecondary}
                />
              </QuestionContainer>
              <QuestionContainer columnLayout>
                <Title text={`${programName} Content Satisfaction`} size={TitleSize.XSMALL} />
                <BodyText
                  text={describeScore('SATISFACTION', scores['SATISFACTION-THERAPY'])}
                  size={BodyTextSize.DEFAULT}
                  color={theme.colors.textSecondary}
                />
              </QuestionContainer>
            </View>
          </TableRow>,
        )
      }

      SUBSCALE_RESULTS_CONFIGS.forEach((config) => {
        renderedRows.push(
          <SubscaleResultsEntry
            {...config}
            scores={scores}
            onViewAnswersClick={onViewButtonClick}
            describeScore={describeScore}
          />,
        )
      })

      renderedRows.push(
        <LeapsResultsEntry scores={scores} onViewAnswersClick={onViewButtonClick} describeScore={describeScore} />,
      )

      renderedRows.push(<RoiResultsEntry scores={scores} onViewAnswersClick={onViewButtonClick} />)

      renderedRows.push(<RoiResultsEntry scores={scores} onViewAnswersClick={onViewButtonClick} isParentReport />)

      // Handle self reflection
      if (selfReflectionText) {
        renderedRows.push(
          <TableRow testID={tID('ClientOutcomes-selfReflectionContainer')} alignTop>
            <AssessmentContentContainer>
              <Title text='Member Self Reflection' size={TitleSize.MEDIUM} />
            </AssessmentContentContainer>
            <ScoreContentContainer />
            <SelfReflectionContentContainer>
              <BodyText text={selfReflectionText} size={BodyTextSize.DEFAULT} color={theme.colors.textPrimary} />
            </SelfReflectionContentContainer>
          </TableRow>,
        )
      }

      return (
        <OutcomesResultsContainer testID={tID('ClientOutcomes-resultsContainer')}>
          <HeaderContainer>
            <AssessmentContentContainer>
              <Title text='Assessment' size={TitleSize.SMALL} />
            </AssessmentContentContainer>
            <ScoreContentContainer>
              <Title text='Score' size={TitleSize.SMALL} />
            </ScoreContentContainer>
            <Title text='Description' size={TitleSize.SMALL} />
          </HeaderContainer>

          {renderedRows.map((row, index) => (
            <React.Fragment key={index}>
              {React.cloneElement(row as React.ReactElement, { isLastItem: index === renderedRows.length - 1 })}
            </React.Fragment>
          ))}
        </OutcomesResultsContainer>
      )
    } else {
      return <></>
    }
  }

  const renderReminder = () => {
    if (!hasOutcomes) return false

    let shouldRemind = false

    let latestOutcomeDate = '0' // Use a string to match response_date type
    for (const outcome of outcomes) {
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      if (outcome.send_at > latestOutcomeDate) {
        // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
        latestOutcomeDate = outcome.send_at
      }
    }
    const requestedOutcomes = outcomes.filter((outcome: Outcome) => !outcome.response_date)
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    requestedOutcomes.sort((a: Outcome, b: Outcome) => b.send_at.localeCompare(a.send_at))

    const shouldDisplayConsentReminder = !isRegistered && !consentSubmitted
    const shouldDisplayOutcomeReminder =
      requestedOutcomes.length !== 0 && latestOutcomeDate === requestedOutcomes[0].send_at
    const shouldDisplayOptOutMessage =
      (isRegistered && consentStatus === false) || (consentStatus === false && consentSubmitted)
    if (shouldDisplayConsentReminder || shouldDisplayOutcomeReminder || shouldDisplayOptOutMessage) {
      shouldRemind = true
    }

    const getReminderMessage = () => {
      const reminderMessage = {
        header: <></>,
        content: <></>,
      }
      let contentString

      if (shouldDisplayOptOutMessage) {
        contentString = `We will not request Outcomes from this client. If he/she is open to submitting Outcomes, please ask him/her to send an email to care@lyrahealth.com.`
        reminderMessage.header = (
          <p data-test-id='ClientOutcomes-Reminder' className={`test-reminder-message' ${styles['no-data-header']}`}>
            <span className={styles['client-first-name']}>{clientFirstName}</span> has opted out of outcomes
          </p>
        )
        reminderMessage.content = <p>{contentString}</p>
      } else if (shouldDisplayConsentReminder || shouldDisplayOutcomeReminder) {
        if (shouldDisplayConsentReminder) {
          contentString = `hasn't submitted the consent we sent by email`
        } else if (shouldDisplayOutcomeReminder) {
          contentString = `hasn't submitted the latest Outcomes we sent by email on ${format(
            parseISO(latestOutcomeDate),
            'MM/dd/yyyy',
          )}`
        }
        reminderMessage.header = (
          <p data-test-id='ClientOutcomes-Reminder' className={`test-reminder-message' ${styles['no-data-header']}`}>
            Please remind <span className={styles['client-first-name']}>{clientFirstName}</span> to submit{' '}
            {shouldDisplayConsentReminder ? 'consent' : 'outcome'}
          </p>
        )
        reminderMessage.content = (
          <p>
            <span className={styles['client-first-name']}>{clientFirstName} </span>
            {contentString}
          </p>
        )
      }
      return reminderMessage
    }

    const reminderMessage = getReminderMessage()
    if (!shouldRemind) return false
    return (
      <div data-test-id='ClientOutcomes-Reminder' className={styles['no-data']}>
        {reminderMessage.header}
        {reminderMessage.content}
      </div>
    )
  }

  const renderOutcomeDropdown = () => {
    const submittedOutcomes = (outcomes ?? []).filter((outcome: Outcome) => outcome.response_date).reverse()
    const hasMultipleTypesOfOutcomes = new Set((outcomes ?? []).map((element) => element.program_name)).size > 1
    const selectOptions = submittedOutcomes.map((outcome, idx) => {
      const { program_name: programName, response_date, date_created } = outcome
      const program = programName ? getProgramDisplayLabel(intl, programName) : ''
      return {
        text: `Outcomes #${idx + 1}${hasMultipleTypesOfOutcomes ? ' ' + program : ''}`,
        accessibleText: `Episode ${idx + 1} ${format(parseISO(response_date), 'MM/dd/yy')}${
          hasMultipleTypesOfOutcomes ? ' ' + program : ''
        }`,
        id: idx.toString(),
        outcome,
        selectHandler: () => setSelectedOutcome(outcome),
        responseDate: format(parseISO(response_date), 'MMM dd, yyyy'),
        createdDate: date_created ? format(parseISO(date_created), 'MMM dd, yyyy') : '',
      }
    })
    const currentSelectedOutcome = selectOptions.find(({ outcome }) => isEqual(outcome, selectedOutcome))
    // @ts-expect-error TS(2790): The operand of a 'delete' operator must be optiona... Remove this comment to see the full error message
    selectOptions.forEach((selectOption) => delete selectOption.outcome)

    const dropdownKey = currentSelectedOutcome
      ? `outcome-${currentSelectedOutcome.id}`
      : `default-${selectOptions[0]?.id || 'empty'}`

    const createAndSubmitDatesAreTheSame = currentSelectedOutcome?.responseDate === currentSelectedOutcome?.createdDate
    const oatmealTextColor = theme.colors.secondaryButtonOatmealTextActive

    const caregiver = recipientOptions?.caregiver
    const recipientDropdownItems =
      recipientOptions && onRecipientChange
        ? [
            {
              text: formatMessage(
                { defaultMessage: '{name} (teen)', description: 'Dropdown label for teen assessment recipient' },
                { name: recipientOptions.teen.name },
              ),
              id: recipientOptions.teen.lyraId,
              selectHandler: () => onRecipientChange(recipientOptions.teen.lyraId),
            },
            ...(caregiver
              ? [
                  {
                    text: formatMessage(
                      {
                        defaultMessage: '{name} (caregiver)',
                        description: 'Dropdown label for caregiver assessment recipient',
                      },
                      { name: caregiver.name },
                    ),
                    id: caregiver.lyraId,
                    selectHandler: () => onRecipientChange(caregiver.lyraId),
                  },
                ]
              : []),
          ]
        : null

    const selectedRecipientItem = recipientDropdownItems?.find((item) => item.id === selectedRecipientLyraId)
    const recipientDropdown = recipientDropdownItems ? (
      <OatmealDropdownButton
        testID={tID('selectRecipient')}
        dropdownItems={recipientDropdownItems}
        preselectedValue={selectedRecipientItem?.text || recipientDropdownItems[0]?.text}
        value={selectedRecipientLyraId}
        buttonType={ButtonType.SECONDARY}
        customTextColor={oatmealTextColor}
        iconColor={oatmealTextColor}
        dropdownStyle={{ width: 200 }}
      />
    ) : null

    if (submittedOutcomes.length === 0) {
      return recipientDropdown ? (
        <SelectOutcomeContainer testID={tID('ClientOutcomes-dropdown')}>
          <DropdownsGroup>{recipientDropdown}</DropdownsGroup>
        </SelectOutcomeContainer>
      ) : null
    }

    return (
      <SelectOutcomeContainer testID={tID('ClientOutcomes-dropdown')}>
        <DropdownsGroup>
          {recipientDropdown}
          <OatmealDropdownButton
            key={dropdownKey}
            testID={tID('selectOutcome')}
            dropdownItems={selectOptions}
            preselectedValue={currentSelectedOutcome?.text || selectOptions[0]?.text}
            buttonType={ButtonType.SECONDARY}
            customTextColor={oatmealTextColor}
            iconColor={oatmealTextColor}
            dropdownStyle={{ width: 160 }}
            customOptionsConfig={
              new TypeAheadCustomOptionsConfig<any>(
                selectOptions,
                (option) => <span>{option.text}</span>,
                undefined,
                'accessibleText',
                undefined,
                ValueFetchMode.ID,
              )
            }
          />
        </DropdownsGroup>

        <DateContainer>
          <BodyText
            text={
              createAndSubmitDatesAreTheSame
                ? formatMessage(
                    {
                      defaultMessage: 'Submitted by {name}',
                      description: 'Label showing who submitted the assessment',
                    },
                    { name: selectedRecipientName ?? clientFirstName },
                  )
                : formatMessage({
                    defaultMessage: 'Assessment started',
                    description: 'Label for when an assessment was started',
                  })
            }
            size={BodyTextSize.CAPTION}
            color={theme.colors.textSecondary}
          />
          <Title
            text={
              createAndSubmitDatesAreTheSame
                ? currentSelectedOutcome?.responseDate
                : currentSelectedOutcome?.createdDate
            }
            size={TitleSize.SMALL}
            color={theme.colors.textPrimary}
          />
        </DateContainer>
        {!createAndSubmitDatesAreTheSame && (
          <DateContainer withLeftBorder>
            <BodyText
              text={formatMessage(
                {
                  defaultMessage: 'Submitted by {name}',
                  description: 'Label showing who submitted the assessment',
                },
                { name: selectedRecipientName ?? clientFirstName },
              )}
              size={BodyTextSize.CAPTION}
              color={theme.colors.textSecondary}
            />
            <Title
              text={currentSelectedOutcome?.responseDate}
              size={TitleSize.SMALL}
              color={theme.colors.textPrimary}
            />
          </DateContainer>
        )}
      </SelectOutcomeContainer>
    )
  }

  const renderOutcomeControls = () => {
    return (
      <OutcomeControlsContainer>
        {assessmentTrackSettings && (
          <AssessmentTrackSettingsContainer>{assessmentTrackSettings}</AssessmentTrackSettingsContainer>
        )}
        {renderOutcomeDropdown()}
      </OutcomeControlsContainer>
    )
  }

  return (
    <div className={styles['select-outcome-container']}>
      {title ? <h2 className={styles['outcome-header']}>{title}</h2> : ''}
      {learnMore && (
        <>
          <p data-testid={tID('ClientDetails-resend-instruction')}>
            Do not resend unless it has been more than 4 weeks.
          </p>
          <div data-test-id='ClientDetails-resend'>
            {loading ? (
              <LoadingIndicator size={30} />
            ) : (
              <SecondaryButton onClick={resendOutcomeClickHandler} disabled={loading}>
                Resend {isRegistered || consentStatus ? 'Outcome' : 'Consent'}
              </SecondaryButton>
            )}
          </div>
          <a
            id='outcomes-learn-more'
            className={styles['learn-more']}
            href='https://provider-support.lyrahealth.com/hc/en-us/articles/115012608808'
            target='_blank'
            rel='noreferrer'
          >
            Learn more
          </a>
        </>
      )}
      {reminder && renderReminder()}
      {renderOutcomeControls()}
      {selectedRecipientName && hasOutcomes && (
        <ResultsHeadingContainer>
          <Title
            testID={tID('ClientOutcomes-recipientHeading')}
            text={
              isCaregiverSelected
                ? formatMessage(
                    {
                      defaultMessage: 'Caregiver assessment results for {name}',
                      description: "heading showing the teen whose caregiver's assessment results are displayed",
                    },
                    { name: recipientOptions?.teen.name },
                  )
                : formatMessage(
                    {
                      defaultMessage: "{name}'s self-assessment results",
                      description: 'heading showing whose assessment results are displayed',
                    },
                    { name: selectedRecipientName },
                  )
            }
            size={TitleSize.MEDIUM}
          />
        </ResultsHeadingContainer>
      )}
      {hasOutcomes ? (
        <>
          {renderOutcomeResults()}
          <OutcomeHistoryCharts
            outcomes={outcomes ?? []}
            clientName={clientFullName ?? ''}
            answerModalAction={showAnswersModal}
          />
        </>
      ) : (
        renderNoDataContent()
      )}
      <BaseModal
        isOpen={showModal}
        closeModal={() => setShowModal(false)}
        // @ts-expect-error TS(2322): Type 'Response | null' is not assignable to type '... Remove this comment to see the full error message
        body={<OutcomeResponseDetails viewData={modalData} />}
        aria-label={
          modalData ? `${modalData.title ?? modalData.assessmentType} - ${modalData.dateSubmitted}` : undefined
        }
      />
    </div>
  )
}

type Response = {
  clientName: string
  title: string | undefined
  dateSubmitted: string
  dateStarted: string
  assessmentType: string
  score: number | string | null | undefined
  answers: QuestionResponse[] | undefined
}

type RecipientOption = {
  lyraId: string
  name: string
}

type RecipientOptions = {
  teen: RecipientOption
  caregiver?: RecipientOption
}

type ClientOutcomesProps = {
  clientFirstName?: string
  clientFullName?: string
  consentStatus?: boolean
  consentSubmitted?: boolean
  isRegistered?: boolean
  learnMore?: boolean
  outcomes?: Outcome[] | undefined
  assignmentsWithSelfReflection?: Assignment[] | undefined
  patient_lyra_id?: string
  providerDetailsData?: ProviderAdminProviderInfo
  reminder?: boolean
  title?: string
  selectedProgressEpisode?: Episode
  assessmentTrackSettings?: React.ReactNode
  recipientOptions?: RecipientOptions
  selectedRecipientLyraId?: string
  onRecipientChange?: (lyraId: string) => void
  actions?: {
    resendOutcomeEmail: (clientID: any, providerId?: string) => any
    addAlert: (alertObj: any) => any
  }
}

const mapStateToProps = (state: RootState): any => {
  return {
    providerDetailsData: getProviderDetailsData(state),
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => {
  return {
    dispatch,
    actions: bindActionCreators({ resendOutcomeEmail, addAlert }, dispatch),
  }
}

export default connect<object, object, ClientOutcomesProps>(mapStateToProps, mapDispatchToProps)(ClientOutcomes)
